import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ACCESS_KEY_TYPE_TRANSLATION_MAP } from 'src/features/accessKey/utils/constants';
import { AccessKeyFormSchemaValues } from 'src/features/accessKey/utils/formSchema';
import { accessKeyMessages } from 'src/features/accessKey/utils/messages';
import { ACCESS_KEY_TYPE } from 'src/features/accessKey/utils/types';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Checkbox } from 'src/registry/ui/Checkbox';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormField, FormMessage } from 'src/registry/ui/Form';

type AccessKeyFormProps = {
    form: UseFormReturn<AccessKeyFormSchemaValues>;
    onSubmit: (values: AccessKeyFormSchemaValues) => void;
};

function AccessKeyForm({ onSubmit, form }: AccessKeyFormProps) {
    const { formatMessage } = useIntl();

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SelectFormField<AccessKeyFormSchemaValues>
                form={form}
                required
                name="type"
                label={formatMessage(accessKeyMessages.type)}
                options={Object.values(ACCESS_KEY_TYPE)}
                optionTranslationMap={ACCESS_KEY_TYPE_TRANSLATION_MAP}
            />

            <InputFormField<AccessKeyFormSchemaValues>
                form={form}
                name="value"
                type="text"
                label={formatMessage(accessKeyMessages.value)}
            />

            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(accessKeyMessages.validFrom)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <DatePickerWithTime
                                        onChange={field.onChange}
                                        dateISO={field.value}
                                        placeholder={`${formatMessage(accessKeyMessages.validFrom)}...`}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(accessKeyMessages.validTo)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <DatePickerWithTime
                                        onChange={field.onChange}
                                        dateISO={field.value}
                                        placeholder={`${formatMessage(accessKeyMessages.validTo)}...`}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="pass_back_allowed"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...accessKeyMessages.passBackAllowed} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Checkbox
                                    checked={!!field.value}
                                    onCheckedChange={(checked) => {
                                        const value = checked === true;
                                        field.onChange(value);
                                    }}
                                />
                            </FormControl>
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default AccessKeyForm;
